import React, { useState, useEffect } from "react";
import { fetchAllOrders } from "../api/order";
import { getReviewsByUserId } from "../api/review";
import { Link } from "react-router-dom";
import { seoDefaults } from "../utils/seoDefaults";
import SEO from "../api/seo";
import OrderViewModal from "../components/OrderViewModal";
import ReviewModal from "../components/ReviewModal";

const OrderPage = () => {
  const [allOrders, setAllOrders] = useState([]);
  const [userReviews, setUserReviews] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedReviewProduct, setSelectedReviewProduct] = useState(null);
  const [selectedReviewMode, setSelectedReviewMode] = useState("add"); // or "view"
  const [selectedReviewData, setSelectedReviewData] = useState(null);

  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const loadOrders = async () => {
      if (!user || !user._id) {
        setError("User not authenticated");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await fetchAllOrders(user._id);
        setAllOrders(data);
        setFilteredOrders(data);
        await loadUserReviews();
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch orders");
        setLoading(false);
      }
    };

    loadOrders();
  }, [user?._id]);

  const loadUserReviews = async () => {
    try {
      const res = await getReviewsByUserId(user._id);
      setUserReviews(res.data);
    } catch (err) {
      console.error("Failed to fetch reviews", err);
    }
  };

  const hasReviewed = (productId) => {
    return userReviews.some((r) => r.product_id === productId);
  };

  const getReviewByProduct = (productId) => {
    return userReviews.find((r) => r.product_id === productId);
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    if (status === "All") {
      setFilteredOrders(allOrders);
    } else {
      const filtered = allOrders.filter(
        (order) => order.order_status === status
      );
      setFilteredOrders(filtered);
    }
  };

  const formatDate = (dateString) => {
    const [datePart] = dateString.split(" ");
    const [day, month, year] = datePart.split("-");
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const monthName = monthNames[parseInt(month, 10) - 1];
    return `${day}-${monthName}-${year}`;
  };

  return (
    <>
      <SEO {...seoDefaults.orders} />
      <div className="min-h-screen bg-gray-950 text-white pt-24 pb-10 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-extrabold text-center mb-6 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">
            My Orders
          </h1>

          <div className="flex flex-wrap gap-3 justify-center mb-8">
            {["All", "Pending", "Processing", "Shipped", "Delivered"].map(
              (status) => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-5 py-2 rounded-full font-semibold transition-transform hover:scale-105 shadow-lg ${
                    selectedStatus === status
                      ? "bg-gradient-to-r from-cyan-500 to-purple-600 text-white"
                      : "bg-gray-800 text-white border border-cyan-600"
                  }`}
                >
                  {status}
                </button>
              )
            )}
          </div>

          {loading ? (
            <div className="text-center py-10 text-white/70">
              Loading orders...
            </div>
          ) : error ? (
            <div className="text-center py-10 text-red-400">{error}</div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-10 text-white/70">
              No orders found
              {selectedStatus !== "All" && ` with ${selectedStatus} status`}.
              <div className="mt-4">
                <Link
                  to="/categories"
                  className="inline-block px-6 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-full shadow hover:scale-105 transition-transform"
                >
                  Start Shopping
                </Link>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto bg-gray-900 rounded-2xl p-6 border border-purple-700 shadow-lg">
              <table className="min-w-full text-sm text-white">
                <thead>
                  <tr className="text-cyan-400 uppercase text-xs border-b border-cyan-700">
                    <th className="p-3 text-left">Order ID</th>
                    <th className="p-3 text-left">Date</th>
                    <th className="p-3 text-left">Items</th>
                    <th className="p-3 text-left">Total</th>
                    <th className="p-3 text-left">Location</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Payment</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr
                      key={order._id}
                      className="border-b border-gray-800 hover:bg-gray-800 transition-colors"
                    >
                      <td className="p-3 font-semibold text-purple-400">
                        #{order.order_no || order._id.slice(-6)}
                      </td>
                      <td className="p-3 text-white/80">
                        {formatDate(order.date_time)}
                      </td>
                      <td className="p-3">
                        <ul className="space-y-1 text-white/80">
                          {order.items.map((item, index) => (
                            <li key={index}>
                              {item.product_name} — {item.quantity} (
                              {item.pieces} pcs) — ₹{item.price}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="p-3 font-bold text-cyan-400">
                        ₹{order.amount}
                      </td>
                      <td className="p-3 text-white/80">
                        {order.location || "N/A"}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            order.order_status === "Pending"
                              ? "bg-yellow-500/20 text-yellow-400"
                              : order.order_status === "Processing"
                              ? "bg-blue-500/20 text-blue-400"
                              : order.order_status === "Shipped"
                              ? "bg-indigo-500/20 text-indigo-400"
                              : order.order_status === "Delivered"
                              ? "bg-green-500/20 text-green-400"
                              : "bg-gray-500/20 text-gray-400"
                          }`}
                        >
                          {order.order_status}
                        </span>
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            order.is_paid === "YES"
                              ? "bg-green-500/20 text-green-400"
                              : "bg-red-500/20 text-red-400"
                          }`}
                        >
                          {order.is_paid === "YES" ? "Paid" : "Unpaid"}
                        </span>
                      </td>

                      <td className="p-3 space-y-2">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setIsViewModalOpen(true);
                          }}
                          className="block w-full text-sm px-4 py-1 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow hover:scale-105 transition-transform"
                        >
                          View Details
                        </button>

                        {order.order_status === "Delivered" && (
                          <button
                            onClick={() => {
                              setSelectedOrder(order);
                              setIsReviewModalOpen(true);
                            }}
                            className="block w-full text-xs px-4 py-1 bg-gray-800 text-purple-400 border border-purple-600 rounded-full hover:bg-purple-500/20 transition"
                          >
                            Review Items
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <OrderViewModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        order={selectedOrder}
      />

      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        order={selectedOrder}
        user={user}
        token={localStorage.getItem("token")}
        fetchAndSetReviews={loadUserReviews}
        userReviews={userReviews}
      />
    </>
  );
};
export default OrderPage;
